/**
 * Normaliza eventos del feed `live-events` al formato canónico consumido por
 * `mapLiveEventsToLineups` (tipos con punto: `lineup.confirmed`, `substitution.made`, etc.).
 *
 * Soporta el contrato BDD (`LINEUP_CONFIRMED`, `SUBSTITUTION`, `YELLOW`, `GOAL`, topics
 * `LINEUPS` / `TIMELINE` / `SCORE`) y el formato stream actual (`lineup.confirmed`, …).
 */

import { getStreamEventDedupeKey } from './liveFeedStreamUtils';
import {
  buildStreamTeamIdToCardTeamIdMap,
  eventMatchesAway,
  eventMatchesHome,
  getMatchTeamRefs,
  resolveEventTeamSide,
} from './matchTeamRefUtils';

const CANONICAL_LINEUP_CONFIRMED = 'lineup.confirmed';
const CANONICAL_SUBSTITUTION = 'substitution.made';
const CANONICAL_CARD = 'card.issued';
const CANONICAL_SCORE = 'score.updated';

const CARD_EVENT_TYPES = new Set([
  'YELLOW',
  'RED_CARD',
  'RED_SECOND_YELLOW',
]);

const CARD_TYPE_FROM_EVENT_TYPE = {
  YELLOW: 'YELLOW',
  RED_CARD: 'RED',
  RED_SECOND_YELLOW: 'SECOND_YELLOW',
};

function normalizeEventTypeKey(raw) {
  if (raw == null) return '';
  return String(raw).trim();
}

function isLineupConfirmedEvent(eventType, topic, payload) {
  const type = normalizeEventTypeKey(eventType).toUpperCase();
  const topicKey = topic != null ? String(topic).trim().toUpperCase() : '';
  if (type === 'LINEUP_CONFIRMED') return true;
  if (topicKey === 'LINEUPS' && payload?.homeLineup && payload?.awayLineup) {
    return true;
  }
  return false;
}

function mapBddSquadPlayerToStream(player, isActive) {
  if (!player) return null;
  return {
    playerId: player.playerId,
    playerName: player.playerName ?? player.name,
    uniformNumber:
      player.uniformNumber != null
        ? player.uniformNumber
        : player.number != null
          ? player.number
          : undefined,
    positionNumber: player.positionNumber ?? player.position,
    isActive,
  };
}

function mapBddLineupBundleToCurrentLineup(bundle) {
  if (!bundle) return null;
  const starters = (Array.isArray(bundle.starters) ? bundle.starters : [])
    .map((p) => mapBddSquadPlayerToStream(p, true))
    .filter(Boolean);
  const bench = (Array.isArray(bundle.substitutes) ? bundle.substitutes : [])
    .map((p) => mapBddSquadPlayerToStream(p, false))
    .filter(Boolean);

  return {
    formation: bundle.formation != null ? String(bundle.formation) : undefined,
    starters,
    bench,
  };
}

function normalizeLineupConfirmedEvent(event) {
  const payload = event?.payload ?? {};

  if (payload.homeLineup && payload.awayLineup) {
    const base = { ...event, eventType: CANONICAL_LINEUP_CONFIRMED };
    return [
      {
        ...base,
        payload: {
          ...payload,
          team: 'HOME_TEAM',
          teamId: payload.homeLineup.teamId,
          currentLineup: mapBddLineupBundleToCurrentLineup(payload.homeLineup),
        },
      },
      {
        ...base,
        payload: {
          ...payload,
          team: 'AWAY_TEAM',
          teamId: payload.awayLineup.teamId,
          currentLineup: mapBddLineupBundleToCurrentLineup(payload.awayLineup),
        },
      },
    ];
  }

  if (payload.currentLineup) {
    return [{ ...event, eventType: CANONICAL_LINEUP_CONFIRMED, payload }];
  }

  const teamId = payload.teamId;
  const side = payload.team;
  const bundle =
    payload.homeLineup ??
    payload.awayLineup ??
    (side === 'HOME_TEAM' || side === 'AWAY_TEAM' ? payload : null);

  if (!bundle && !teamId) return [event];

  return [
    {
      ...event,
      eventType: CANONICAL_LINEUP_CONFIRMED,
      payload: {
        ...payload,
        team: side ?? payload.team,
        teamId: teamId ?? bundle?.teamId,
        currentLineup:
          payload.currentLineup ?? mapBddLineupBundleToCurrentLineup(bundle),
      },
    },
  ];
}

function normalizeSubstitutionParticipant(participant) {
  if (!participant) return participant;
  const name = participant.playerName ?? participant.name;
  return {
    ...participant,
    playerId: participant.playerId,
    playerName: name != null ? String(name) : undefined,
    uniformNumber:
      participant.uniformNumber != null
        ? participant.uniformNumber
        : participant.number != null
          ? participant.number
          : undefined,
  };
}

function normalizeSubstitutionEvent(event) {
  const payload = event?.payload ?? {};
  const teamId =
    payload.teamId ??
    payload.substitutionTeamId ??
    payload.playerOut?.teamId;

  return {
    ...event,
    eventType: CANONICAL_SUBSTITUTION,
    payload: {
      ...payload,
      teamId,
      team: payload.team,
      minute: payload.minute,
      playerOut: normalizeSubstitutionParticipant(payload.playerOut),
      playerIn: normalizeSubstitutionParticipant(payload.playerIn),
    },
  };
}

function resolveCardTypeFromPayload(payload, eventType) {
  if (payload?.cardType != null) {
    const cardType = String(payload.cardType).trim().toUpperCase();
    if (cardType === 'RED_CARD') return 'RED';
    if (cardType === 'RED_SECOND_YELLOW') return 'SECOND_YELLOW';
    return cardType;
  }
  const typeKey = normalizeEventTypeKey(eventType).toUpperCase();
  return CARD_TYPE_FROM_EVENT_TYPE[typeKey] ?? typeKey;
}

function normalizeCardEvent(event) {
  const payload = event?.payload ?? {};
  const eventType = normalizeEventTypeKey(event?.eventType).toUpperCase();
  const cardType = resolveCardTypeFromPayload(payload, eventType);

  const playerName =
    payload.playerName ??
    payload.playerOffended?.playerName ??
    payload.participantName;

  return {
    ...event,
    eventType: CANONICAL_CARD,
    payload: {
      ...payload,
      cardType,
      teamId: payload.teamId,
      team: payload.team,
      minute: payload.minute,
      playerOffended: {
        playerId:
          payload.playerId ??
          payload.playerOffended?.playerId,
        playerName:
          playerName != null ? String(playerName) : undefined,
      },
    },
  };
}

function normalizeGoalEvent(event) {
  const payload = event?.payload ?? {};
  const scorer = payload.scorer ?? {};
  const playerName =
    payload.playerName ??
    scorer.playerName ??
    scorer.name ??
    payload.scorerName;

  const normalized = {
    ...event,
    eventType: CANONICAL_SCORE,
    payload: {
      ...payload,
      scoringTeam: payload.scoringTeam,
      scoringTeamId:
        payload.scoringTeamId ??
        payload.scorerTeamId ??
        payload.teamId,
      minute: payload.minute,
      homeScore: payload.homeScore,
      awayScore: payload.awayScore,
      scorer: {
        playerId: scorer.playerId ?? payload.playerId ?? payload.scorerPlayerId,
        playerName:
          playerName != null ? String(playerName) : undefined,
      },
      assistedBy:
        payload.assistedBy != null ? String(payload.assistedBy) : undefined,
    },
  };

  return normalized;
}

/**
 * @param {object} event — evento crudo del stream
 * @returns {object|object[]|null}
 */
export function normalizeStreamEvent(event) {
  if (!event) return null;

  const rawType = normalizeEventTypeKey(event.eventType);
  const typeUpper = rawType.toUpperCase();
  const topic =
    event.topic != null ? String(event.topic).trim().toUpperCase() : '';
  const payload = event.payload ?? {};

  if (isLineupConfirmedEvent(rawType, topic, payload)) {
    return normalizeLineupConfirmedEvent(event);
  }

  if (
    typeUpper === 'SUBSTITUTION' ||
    rawType === CANONICAL_SUBSTITUTION
  ) {
    return normalizeSubstitutionEvent(event);
  }

  if (
    CARD_EVENT_TYPES.has(typeUpper) ||
    rawType === CANONICAL_CARD ||
    (topic === 'TIMELINE' && (payload.cardType || CARD_EVENT_TYPES.has(typeUpper)))
  ) {
    return normalizeCardEvent(event);
  }

  if (
    typeUpper === 'GOAL' ||
    rawType === CANONICAL_SCORE ||
    (topic === 'SCORE' && payload.homeScore != null && payload.awayScore != null)
  ) {
    return normalizeGoalEvent(event);
  }

  if (rawType === CANONICAL_LINEUP_CONFIRMED) {
    return normalizeLineupConfirmedEvent(event);
  }

  return event;
}

/**
 * @param {object[]} events
 * @returns {object[]}
 */
export function normalizeStreamEventsList(events) {
  if (!Array.isArray(events)) return [];

  const normalized = [];
  events.forEach((event) => {
    const result = normalizeStreamEvent(event);
    if (result == null) return;
    if (Array.isArray(result)) {
      normalized.push(...result);
    } else {
      normalized.push(result);
    }
  });
  return normalized;
}

function normalizePlayerDetailsList(details) {
  if (Array.isArray(details)) return details.filter(Boolean);
  if (details && typeof details === 'object') return [details];
  return [];
}

function isOwnGoalPlayerDetail(detail) {
  if (!detail) return false;
  if (detail.type === 'OWN_GOAL') return true;
  return /gol\s+en\s+contra|propia\s+puerta/i.test(String(detail?.name ?? ''));
}

function countPlayerGoalDetails(player) {
  return normalizePlayerDetailsList(player?.playerMatchDetails).filter(
    (detail) => detail?.type === 'GOAL' && !isOwnGoalPlayerDetail(detail),
  ).length;
}

function countPlayerOwnGoalDetails(player) {
  return normalizePlayerDetailsList(player?.playerMatchDetails).filter(
    isOwnGoalPlayerDetail,
  ).length;
}

/**
 * Cuenta iconos `GOAL` en `lineups` (misma fuente que cancha / tabla de alineaciones).
 *
 * @param {object[]|null|undefined} lineups
 * @param {object[]} [matchCard]
 * @returns {{ home: number, away: number }}
 */
export function countGoalsStoredInLineups(lineups, matchCard) {
  const card = Array.isArray(matchCard) ? matchCard[0] : null;
  const refs = getMatchTeamRefs(card?.summary?.matchDetails);
  let home = 0;
  let away = 0;

  if (!Array.isArray(lineups)) {
    return { home, away };
  }

  lineups.forEach((lineup) => {
    const squad = [
      ...(Array.isArray(lineup?.startingXI) ? lineup.startingXI : []),
      ...(Array.isArray(lineup?.substitutes) ? lineup.substitutes : []),
    ];
    let sideGoals = 0;
    let sideOwnGoals = 0;
    squad.forEach((player) => {
      sideGoals += countPlayerGoalDetails(player);
      sideOwnGoals += countPlayerOwnGoalDetails(player);
    });

    if (eventMatchesHome(lineup?.teamId, refs)) {
      home += sideGoals;
      away += sideOwnGoals;
    } else if (eventMatchesAway(lineup?.teamId, refs)) {
      away += sideGoals;
      home += sideOwnGoals;
    }
  });

  return { home, away };
}

function parseStreamScorePayload(streamEvents) {
  if (!Array.isArray(streamEvents) || streamEvents.length === 0) {
    return { home: null, away: null };
  }

  const events = normalizeStreamEventsList(streamEvents);
  let streamHome = null;
  let streamAway = null;

  events.forEach((ev) => {
    if (ev?.eventType !== CANONICAL_SCORE) return;
    const payload = ev.payload;
    if (payload?.homeScore == null || payload?.awayScore == null) return;

    const home = Number(payload.homeScore);
    const away = Number(payload.awayScore);
    if (!Number.isNaN(home) && !Number.isNaN(away)) {
      streamHome = home;
      streamAway = away;
    }
  });

  return { home: streamHome, away: streamAway };
}

/**
 * Ignora `score.updated` de un batch incremental cuando el marcador del stream es menor
 * en ambos lados que el de `match-details` (p. ej. poll solo con autogol 0-1 en partido 7-4).
 */
function resolveAuthoritativeStreamScores(streamScores, currentHome, currentAway) {
  if (streamScores.home == null || streamScores.away == null) {
    return { home: null, away: null };
  }

  const streamIsBehindOnBoth =
    streamScores.home < currentHome && streamScores.away < currentAway;
  if (streamIsBehindOnBoth) {
    return { home: null, away: null };
  }

  return streamScores;
}

/**
 * @param {object} card
 * @param {{ home: number, away: number }} scores
 */
function writeMatchCardScores(patch, card, scores) {
  const md = card.summary?.matchDetails;
  if (!md) return;

  patch.matchCard = [
    {
      ...card,
      summary: {
        ...card.summary,
        matchDetails: {
          ...md,
          home: { ...md.home, score: scores.home },
          away: { ...md.away, score: scores.away },
        },
      },
    },
  ];
}

/**
 * Marcador solo desde el acumulado de `live-events` (sin baseline ni goles de `match-details`).
 * @param {object[]|null|undefined} streamEvents
 * @param {object[]} currentMatchCard
 * @returns {{ home: number, away: number }|null}
 */
function resolveScoreFromLiveEventsStreamOnly(streamEvents, currentMatchCard) {
  if (!Array.isArray(streamEvents) || streamEvents.length === 0) {
    return null;
  }
  if (!Array.isArray(currentMatchCard) || !currentMatchCard[0]) {
    return null;
  }

  const streamScores = parseStreamScorePayload(streamEvents);
  if (streamScores.home != null && streamScores.away != null) {
    return { home: streamScores.home, away: streamScores.away };
  }

  const actionCredits = countGoalCreditsFromStreamActions(
    streamEvents,
    currentMatchCard,
    { ignoreLineupsKnownGoals: true },
  );
  if (actionCredits.home > 0 || actionCredits.away > 0) {
    return { home: actionCredits.home, away: actionCredits.away };
  }

  return null;
}

/**
 * Construye listas de `goalsSummary` únicamente desde eventos del stream `live-events`.
 * @returns {{ homeGoals: object[], awayGoals: object[], changed: boolean }}
 */
function buildGoalsSummaryListsFromStreamEvents(streamEvents, currentMatchCard) {
  const homeGoals = [];
  const awayGoals = [];
  if (!Array.isArray(currentMatchCard) || !currentMatchCard[0]) {
    return { homeGoals, awayGoals, changed: false };
  }

  const card = currentMatchCard[0];
  const md = card.summary?.matchDetails;
  if (!md) return { homeGoals, awayGoals, changed: false };

  const refs = getMatchTeamRefs(md);
  const events = normalizeStreamEventsList(
    Array.isArray(streamEvents) ? streamEvents : [],
  );
  const streamTeamIdMap = buildStreamTeamIdToCardTeamIdMap(
    events,
    refs.homeTeamId,
    refs.awayTeamId,
  );
  const knownKeys = new Set();
  let changed = appendGoalsFromScoreStreamEvents(events, {
    homeGoals,
    awayGoals,
    knownKeys,
    refs,
    streamTeamIdMap,
  });

  events.forEach((event) => {
    if (event?.eventType !== 'action.recorded') return;

    const entry = buildGoalSummaryEntryFromActionEvent(event);
    if (!entry) return;

    const rawType =
      event.payload?.type != null
        ? String(event.payload.type).trim().toUpperCase()
        : '';
    const side = resolveActionTeamSide(
      event.payload?.teamId,
      event.payload?.team,
      refs,
      streamTeamIdMap,
    );
    if (!side) return;

    const isOwnGoal = rawType === 'OWN_GOAL';
    const creditSide = isOwnGoal
      ? side === 'home'
        ? 'away'
        : 'home'
      : side;
    const targetList = creditSide === 'home' ? homeGoals : awayGoals;

    if (appendGoalSummaryEntry(targetList, knownKeys, entry)) {
      changed = true;
    }
  });

  return { homeGoals, awayGoals, changed };
}

/**
 * Reconcilia el marcador con el stream, los goles en `lineups` y el baseline de `match-details`.
 * Nunca muestra menos que el score del fixture; ignora batches incrementales del stream
 * cuyo `score.updated` va por detrás en local y visita (p. ej. solo autogol 0-1 en un 7-4).
 *
 * @param {Record<string, unknown>} patch
 * @param {object[]} [currentMatchCard]
 * @param {{ streamEvents?: object[], lineups?: object[]|null, ignoreMatchDetailsBaseline?: boolean }} [options]
 */
export function reconcileMatchCardScoreWithStoredGoals(
  patch,
  currentMatchCard,
  options = {},
) {
  if (!Array.isArray(currentMatchCard) || !currentMatchCard[0]) return;

  const card = patch.matchCard?.[0] ?? currentMatchCard[0];
  const md = card.summary?.matchDetails;
  if (!md) return;

  if (options.ignoreMatchDetailsBaseline) {
    const streamOnly = resolveScoreFromLiveEventsStreamOnly(
      options.streamEvents,
      patch.matchCard ?? currentMatchCard,
    );
    if (!streamOnly) return;
    if (md.home?.score === streamOnly.home && md.away?.score === streamOnly.away) {
      return;
    }
    writeMatchCardScores(patch, card, streamOnly);
    return;
  }

  const currentHome = Number(md.home?.score ?? 0);
  const currentAway = Number(md.away?.score ?? 0);
  const baselineHome = Number.isNaN(currentHome) ? 0 : currentHome;
  const baselineAway = Number.isNaN(currentAway) ? 0 : currentAway;

  const streamScores = resolveAuthoritativeStreamScores(
    parseStreamScorePayload(options.streamEvents),
    baselineHome,
    baselineAway,
  );
  const storedScores = countGoalsStoredInLineups(
    options.lineups,
    patch.matchCard ?? currentMatchCard,
  );

  const actionCredits = countGoalCreditsFromStreamActions(
    options.streamEvents,
    patch.matchCard ?? currentMatchCard,
    { lineups: options.lineups },
  );

  const homeScore = Math.max(
    streamScores.home ?? 0,
    storedScores.home,
    baselineHome,
    baselineHome + actionCredits.home,
  );
  const awayScore = Math.max(
    streamScores.away ?? 0,
    storedScores.away,
    baselineAway,
    baselineAway + actionCredits.away,
  );

  const hasStreamScore =
    streamScores.home != null && streamScores.away != null;
  const hasStoredGoals = storedScores.home > 0 || storedScores.away > 0;
  const hasActionGoals = actionCredits.home > 0 || actionCredits.away > 0;

  if (!hasStreamScore && !hasStoredGoals && !hasActionGoals) return;
  if (md.home?.score === homeScore && md.away?.score === awayScore) return;

  writeMatchCardScores(patch, card, { home: homeScore, away: awayScore });
}

/**
 * Aplica el marcador del stream y lo reconcilia con goles almacenados en `lineups`.
 *
 * @param {Record<string, unknown>} patch
 * @param {object[]} streamEvents — eventos crudos o ya normalizados
 * @param {object[]} [currentMatchCard]
 * @param {{ lineups?: object[]|null, ignoreMatchDetailsBaseline?: boolean }} [options]
 */
export function patchMatchScoreFromStreamEvents(
  patch,
  streamEvents,
  currentMatchCard,
  options = {},
) {
  reconcileMatchCardScoreWithStoredGoals(patch, currentMatchCard, {
    streamEvents,
    lineups: options.lineups,
    ignoreMatchDetailsBaseline: options.ignoreMatchDetailsBaseline,
  });
}

function goalSummaryDedupeKey(goal) {
  const playerId = goal?.player?.playerId ?? '';
  const minute = goal?.minute ?? '';
  const type = goal?.type ?? '';
  if (playerId !== '' || minute !== '' || type !== '') {
    return `p:${playerId}:${minute}:${type}`;
  }
  if (goal?.eventId != null && String(goal.eventId).trim() !== '') {
    return `e:${String(goal.eventId).trim()}`;
  }
  const name = goal?.player?.name ?? '';
  return `n:${name}:${minute}:${type}`;
}

function buildGoalSummaryEntryFromScoreEvent(event) {
  const payload = event?.payload;
  if (!payload) return null;

  const scorer = payload.scorer ?? {};
  const playerName =
    scorer.playerName ??
    scorer.name ??
    payload.playerName ??
    payload.scorerName;

  if (playerName == null || String(playerName).trim() === '') return null;

  const minute = payload.minute;
  const isPenalty = Boolean(payload.penalty);
  const isOwnGoal = Boolean(payload.ownGoal);
  const type = isPenalty ? 'PENALTY' : isOwnGoal ? 'OWN_GOAL' : 'GOAL';
  const playerNumber =
    scorer.playerNumber ??
    scorer.uniformNumber ??
    scorer.number ??
    payload.playerNumber;

  return {
    eventId: event.eventId ?? event.streamEventId ?? null,
    player: {
      playerId: scorer.playerId ?? payload.playerId ?? null,
      name: String(playerName).trim(),
      number:
        playerNumber != null && !Number.isNaN(Number(playerNumber))
          ? Number(playerNumber)
          : undefined,
    },
    minute: minute != null && !Number.isNaN(Number(minute)) ? Number(minute) : undefined,
    minuteDisplay: minute != null ? String(minute) : undefined,
    type,
    name: isPenalty ? 'Penal' : isOwnGoal ? 'Gol en contra' : 'Gol',
  };
}

function sortGoalsSummaryByMinute(goalsSummary) {
  return [...goalsSummary].sort(
    (a, b) => Number(a?.minute ?? 0) - Number(b?.minute ?? 0),
  );
}

function resolveScoringSideFromPayload(payload, refs, streamTeamIdMap) {
  return resolveEventTeamSide(
    payload?.scoringTeamId ?? payload?.scorerTeamId,
    payload?.scoringTeam,
    refs,
    streamTeamIdMap,
  );
}

/**
 * Lado que recibe el gol según el delta de `homeScore`/`awayScore` tras el evento.
 * El stream a veces trae `scorerTeamId` del jugador sin coincidir con quién subió en el marcador.
 *
 * @param {object} payload
 * @param {{ home: number, away: number }} [previousScores]
 * @returns {'home'|'away'|null}
 */
function resolveScoringSideFromGoalScoreDelta(payload, previousScores = { home: 0, away: 0 }) {
  if (payload?.homeScore == null || payload?.awayScore == null) {
    return null;
  }

  const home = Number(payload.homeScore);
  const away = Number(payload.awayScore);
  if (Number.isNaN(home) || Number.isNaN(away)) {
    return null;
  }

  const prevHome = Number(previousScores?.home ?? 0);
  const prevAway = Number(previousScores?.away ?? 0);
  const homeDelta = home - prevHome;
  const awayDelta = away - prevAway;

  if (homeDelta > 0 && awayDelta === 0) return 'home';
  if (awayDelta > 0 && homeDelta === 0) return 'away';
  if (homeDelta > 0 && awayDelta > 0) {
    return homeDelta >= awayDelta ? 'home' : 'away';
  }

  return null;
}

/**
 * @param {object} payload
 * @param {ReturnType<typeof getMatchTeamRefs>} refs
 * @param {Map<string, string>} streamTeamIdMap
 * @param {{ home: number, away: number }} [previousScores]
 * @returns {'home'|'away'|null}
 */
function resolveScoringSideFromGoalPayload(
  payload,
  refs,
  streamTeamIdMap,
  previousScores = { home: 0, away: 0 },
) {
  const sideFromScore = resolveScoringSideFromGoalScoreDelta(payload, previousScores);
  if (sideFromScore) return sideFromScore;

  const sideFromTeam = resolveScoringSideFromPayload(payload, refs, streamTeamIdMap);
  if (!sideFromTeam) return null;

  if (Boolean(payload?.ownGoal)) {
    return sideFromTeam === 'home' ? 'away' : 'home';
  }

  return sideFromTeam;
}

/**
 * @param {object[]} events — ya normalizados
 * @param {object} ctx
 * @returns {boolean} hubo cambios
 */
function appendGoalsFromScoreStreamEvents(
  events,
  { homeGoals, awayGoals, knownKeys, refs, streamTeamIdMap },
) {
  let prevHome = 0;
  let prevAway = 0;
  let changed = false;

  events.forEach((event) => {
    if (event?.eventType !== CANONICAL_SCORE) return;

    const payload = event.payload ?? {};
    const entry = buildGoalSummaryEntryFromScoreEvent(event);
    if (!entry) return;

    const side = resolveScoringSideFromGoalPayload(
      payload,
      refs,
      streamTeamIdMap,
      { home: prevHome, away: prevAway },
    );
    if (!side) return;

    const targetList = side === 'home' ? homeGoals : awayGoals;
    if (appendGoalSummaryEntry(targetList, knownKeys, entry)) {
      changed = true;
    }

    const home = payload.homeScore != null ? Number(payload.homeScore) : null;
    const away = payload.awayScore != null ? Number(payload.awayScore) : null;
    if (home != null && away != null && !Number.isNaN(home) && !Number.isNaN(away)) {
      prevHome = home;
      prevAway = away;
    }
  });

  return changed;
}

function resolveActionTeamSide(payload, md, streamTeamIdMap) {
  const refs = getMatchTeamRefs(md);
  return resolveEventTeamSide(payload?.teamId, payload?.team, refs, streamTeamIdMap);
}

function resolveMatchDetailsFromCardArg(matchCard) {
  if (Array.isArray(matchCard)) {
    return matchCard[0]?.summary?.matchDetails ?? null;
  }
  return matchCard?.summary?.matchDetails ?? null;
}

function normalizeGoalsSummaryList(goalsSummary) {
  if (!Array.isArray(goalsSummary)) return [];
  return goalsSummary.map((goal) => ({ ...goal }));
}

function appendGoalSummaryEntry(targetList, knownKeys, entry) {
  const key = goalSummaryDedupeKey(entry);
  if (knownKeys.has(key)) return false;
  knownKeys.add(key);
  targetList.push(entry);
  return true;
}

function goalSummaryEntryFromLineupGoal(player, detail) {
  if (!player || (detail?.type !== 'GOAL' && detail?.type !== 'OWN_GOAL')) {
    return null;
  }

  const minute = detail.minute;
  const isPenalty = detail?.name === 'Penal';
  const isOwnGoal =
    detail?.type === 'OWN_GOAL' ||
    /gol\s+en\s+contra|propia\s+puerta/i.test(String(detail?.name ?? ''));
  const type = isPenalty ? 'PENALTY' : isOwnGoal ? 'OWN_GOAL' : 'GOAL';

  return {
    eventId: detail?.eventId ?? null,
    player: {
      playerId: player.playerId ?? null,
      name: player.name != null ? String(player.name).trim() : '',
      number:
        player.number != null && !Number.isNaN(Number(player.number))
          ? Number(player.number)
          : undefined,
    },
    minute: minute != null && !Number.isNaN(Number(minute)) ? Number(minute) : undefined,
    minuteDisplay: minute != null ? String(minute) : undefined,
    type,
    name: isPenalty ? 'Penal' : isOwnGoal ? 'Gol en contra' : 'Gol',
  };
}

/**
 * Añade a `goalsSummary` los goles visibles en `lineups` que aún no estén listados
 * (p. ej. gol baseline en cancha que no vino en el stream).
 */
function mergeGoalsSummaryFromLineups(homeGoals, awayGoals, knownKeys, lineups, matchCard) {
  if (!Array.isArray(lineups) || lineups.length === 0) return false;

  const md = resolveMatchDetailsFromCardArg(matchCard);
  const refs = getMatchTeamRefs(md);
  let changed = false;

  lineups.forEach((lineup) => {
    let side = null;
    if (eventMatchesHome(lineup?.teamId, refs)) side = 'home';
    else if (eventMatchesAway(lineup?.teamId, refs)) side = 'away';
    if (!side) return;

    const squad = [
      ...(Array.isArray(lineup?.startingXI) ? lineup.startingXI : []),
      ...(Array.isArray(lineup?.substitutes) ? lineup.substitutes : []),
    ];

    squad.forEach((player) => {
      const details = Array.isArray(player?.playerMatchDetails)
        ? player.playerMatchDetails
        : player?.playerMatchDetails
          ? [player.playerMatchDetails]
          : [];

      details.forEach((detail) => {
        const entry = goalSummaryEntryFromLineupGoal(player, detail);
        if (!entry?.player?.name) return;

        const isOwnGoal =
          detail?.type === 'OWN_GOAL' || entry.type === 'OWN_GOAL';
        const creditSide = isOwnGoal
          ? side === 'home'
            ? 'away'
            : 'home'
          : side;
        const targetList = creditSide === 'home' ? homeGoals : awayGoals;

        if (appendGoalSummaryEntry(targetList, knownKeys, entry)) {
          changed = true;
        }
      });
    });
  });

  return changed;
}

/**
 * Fusiona goles de live-events en `goalsSummary` del `matchCard` (WCMatchHeader),
 * sin duplicar entradas ya presentes ni las reflejadas en `lineups`.
 *
 * @param {Record<string, unknown>} patch
 * @param {object[]} streamEvents
 * @param {object[]} [currentMatchCard]
 * @param {{ lineups?: object[]|null, ignoreMatchDetailsBaseline?: boolean }} [options]
 */
export function patchGoalsSummaryFromStreamEvents(
  patch,
  streamEvents,
  currentMatchCard,
  options = {},
) {
  if (!Array.isArray(currentMatchCard) || !currentMatchCard[0]) return;

  const card = patch.matchCard?.[0] ?? currentMatchCard[0];
  const md = card.summary?.matchDetails;
  if (!md) return;

  if (options.ignoreMatchDetailsBaseline) {
    const { homeGoals, awayGoals } = buildGoalsSummaryListsFromStreamEvents(
      streamEvents,
      patch.matchCard ?? currentMatchCard,
    );
    const sortedHome = sortGoalsSummaryByMinute(homeGoals);
    const sortedAway = sortGoalsSummaryByMinute(awayGoals);

    patch.matchCard = [
      {
        ...card,
        summary: {
          ...card.summary,
          matchDetails: {
            ...md,
            home: { ...md.home, goalsSummary: sortedHome },
            away: { ...md.away, goalsSummary: sortedAway },
          },
        },
      },
    ];
    return;
  }

  const refs = getMatchTeamRefs(md);
  const events = normalizeStreamEventsList(
    Array.isArray(streamEvents) ? streamEvents : [],
  );
  const streamTeamIdMap = buildStreamTeamIdToCardTeamIdMap(
    events,
    refs.homeTeamId,
    refs.awayTeamId,
  );

  const homeGoals = normalizeGoalsSummaryList(md.home?.goalsSummary);
  const awayGoals = normalizeGoalsSummaryList(md.away?.goalsSummary);
  const knownKeys = new Set();
  [...homeGoals, ...awayGoals].forEach((goal) => {
    knownKeys.add(goalSummaryDedupeKey(goal));
  });

  let changed = appendGoalsFromScoreStreamEvents(events, {
    homeGoals,
    awayGoals,
    knownKeys,
    refs,
    streamTeamIdMap,
  });

  events.forEach((event) => {
    if (event?.eventType !== 'action.recorded') return;

    const entry = buildGoalSummaryEntryFromActionEvent(event);
    if (!entry) return;

    const rawType =
      event.payload?.type != null
        ? String(event.payload.type).trim().toUpperCase()
        : '';
    const side = resolveActionTeamSide(
      event.payload?.teamId,
      event.payload?.team,
      refs,
      streamTeamIdMap,
    );
    if (!side) return;

    const isOwnGoal = rawType === 'OWN_GOAL';
    const creditSide = isOwnGoal
      ? side === 'home'
        ? 'away'
        : 'home'
      : side;
    const targetList = creditSide === 'home' ? homeGoals : awayGoals;

    if (appendGoalSummaryEntry(targetList, knownKeys, entry)) {
      changed = true;
    }
  });

  if (
    mergeGoalsSummaryFromLineups(
      homeGoals,
      awayGoals,
      knownKeys,
      options.lineups,
      patch.matchCard ?? currentMatchCard,
    )
  ) {
    changed = true;
  }

  if (!changed) return;

  const nextMd = {
    ...md,
    home: {
      ...md.home,
      goalsSummary: sortGoalsSummaryByMinute(homeGoals),
    },
    away: {
      ...md.away,
      goalsSummary: sortGoalsSummaryByMinute(awayGoals),
    },
  };

  patch.matchCard = [
    {
      ...card,
      summary: {
        ...card.summary,
        matchDetails: nextMd,
      },
    },
  ];
}

const GOAL_ACTION_TYPES = new Set(['GOAL', 'PENALTY_GOAL', 'OWN_GOAL']);

function collectKnownGoalActionIdsFromLineups(lineups) {
  const ids = new Set();
  if (!Array.isArray(lineups)) return ids;

  lineups.forEach((lineup) => {
    const squad = [
      ...(Array.isArray(lineup?.startingXI) ? lineup.startingXI : []),
      ...(Array.isArray(lineup?.substitutes) ? lineup.substitutes : []),
    ];
    squad.forEach((player) => {
      const details = Array.isArray(player?.playerMatchDetails)
        ? player.playerMatchDetails
        : player?.playerMatchDetails
          ? [player.playerMatchDetails]
          : [];
      details.forEach((detail) => {
        if (detail?.type !== 'GOAL' && detail?.type !== 'OWN_GOAL') return;
        const eventId =
          detail?.eventId != null ? String(detail.eventId).trim() : '';
        if (eventId) ids.add(eventId);
      });
    });
  });

  return ids;
}

/**
 * Goles nuevos en el batch de `live-events` vía `action.recorded` (sin `score.updated`).
 * Ignora acciones ya reflejadas en `lineups` y duplicados por `eventId` / `streamEventId` / `actionId`.
 *
 * @param {object[]|null|undefined} streamEvents
 * @param {object[]} [currentMatchCard]
 * @param {{ lineups?: object[]|null, ignoreLineupsKnownGoals?: boolean }} [options]
 * @returns {{ home: number, away: number }}
 */
function countGoalCreditsFromStreamActions(streamEvents, currentMatchCard, options = {}) {
  let home = 0;
  let away = 0;

  if (!Array.isArray(streamEvents) || streamEvents.length === 0) {
    return { home, away };
  }
  if (!Array.isArray(currentMatchCard) || !currentMatchCard[0]) {
    return { home, away };
  }

  const md = currentMatchCard[0].summary?.matchDetails;
  if (!md) return { home, away };

  const refs = getMatchTeamRefs(md);
  const streamTeamIdMap = buildStreamTeamIdToCardTeamIdMap(
    streamEvents,
    refs.homeTeamId,
    refs.awayTeamId,
  );
  const knownActionIds = options.ignoreLineupsKnownGoals
    ? new Set()
    : collectKnownGoalActionIdsFromLineups(options.lineups);
  const creditedKeys = new Set();

  normalizeStreamEventsList(streamEvents).forEach((event) => {
    if (event?.eventType !== 'action.recorded') return;

    const dedupeKey = getStreamEventDedupeKey(event);
    if (dedupeKey) {
      if (creditedKeys.has(dedupeKey)) return;
      creditedKeys.add(dedupeKey);
    }

    const payload = event?.payload;
    if (!payload || typeof payload !== 'object') return;

    const actionId =
      payload.actionId != null ? String(payload.actionId).trim() : '';
    if (actionId && knownActionIds.has(actionId)) return;

    const rawType =
      payload.type != null ? String(payload.type).trim().toUpperCase() : '';
    if (!GOAL_ACTION_TYPES.has(rawType)) return;

    const side = resolveActionTeamSide(payload, md, streamTeamIdMap);
    const creditHome =
      side === 'home' ? rawType !== 'OWN_GOAL' : side === 'away' && rawType === 'OWN_GOAL';
    const creditAway =
      side === 'away' ? rawType !== 'OWN_GOAL' : side === 'home' && rawType === 'OWN_GOAL';

    if (creditHome) home += 1;
    else if (creditAway) away += 1;
  });

  return { home, away };
}

function buildGoalSummaryEntryFromActionEvent(event) {
  const payload = event?.payload;
  if (!payload) return null;

  const playerName = payload.participantName ?? payload.playerName;
  if (playerName == null || String(playerName).trim() === '') return null;

  const rawType =
    payload.type != null ? String(payload.type).trim().toUpperCase() : '';
  const isOwnGoal = rawType === 'OWN_GOAL';
  const isPenalty = rawType === 'PENALTY_GOAL';
  const minute = payload.minute;
  const playerNumber = payload.participantNumber ?? payload.playerNumber;

  return {
    eventId:
      payload.actionId != null
        ? String(payload.actionId)
        : event.eventId ?? event.streamEventId ?? null,
    player: {
      playerId: payload.participantId ?? payload.playerId ?? null,
      name: String(playerName).trim(),
      number:
        playerNumber != null && !Number.isNaN(Number(playerNumber))
          ? Number(playerNumber)
          : undefined,
    },
    minute: minute != null && !Number.isNaN(Number(minute)) ? Number(minute) : undefined,
    minuteDisplay:
      payload.minuteDisplay != null
        ? String(payload.minuteDisplay)
        : minute != null
          ? String(minute)
          : undefined,
    type: isPenalty ? 'PENALTY' : isOwnGoal ? 'OWN_GOAL' : 'GOAL',
    name: isPenalty
      ? 'Penal'
      : isOwnGoal
        ? 'Gol en contra'
        : payload.displayName != null
          ? String(payload.displayName).trim()
          : 'Gol',
  };
}

/**
 * @param {Array<{ events?: object[] }>|null|undefined} liveEventsBlocks
 * @returns {Set<string>}
 */
export function collectGoalActionIdsFromLiveEvents(liveEventsBlocks) {
  const ids = new Set();
  const block = Array.isArray(liveEventsBlocks) ? liveEventsBlocks[0] : null;
  (block?.events ?? []).forEach((event) => {
    if (event?.type !== 'GOAL' && event?.type !== 'OWN_GOAL') return;
    if (event.eventId != null && String(event.eventId).trim() !== '') {
      ids.add(String(event.eventId).trim());
    }
  });
  return ids;
}

/**
 * Incrementa el marcador en `matchCard` con goles nuevos del stream `live-match-actions`
 * (`action.recorded` + `type: GOAL`). Si el evento trae `homeScore`/`awayScore`, se usa ese valor.
 *
 * @param {Record<string, unknown>} patch
 * @param {object[]} streamEvents — eventos crudos del batch de polling
 * @param {object[]} [currentMatchCard]
 * @param {{ skipGoalActionIds?: Set<string>, lineups?: object[]|null }} [options]
 */
export function patchMatchScoreFromLiveMatchActions(
  patch,
  streamEvents,
  currentMatchCard,
  options = {},
) {
  if (!Array.isArray(streamEvents) || streamEvents.length === 0) return;
  if (!Array.isArray(currentMatchCard) || !currentMatchCard[0]) return;

  const skipGoalActionIds = options.skipGoalActionIds ?? new Set();

  const card = patch.matchCard?.[0] ?? currentMatchCard[0];
  const md = card.summary?.matchDetails;
  if (!md) return;

  let homeScore = Number(md.home?.score ?? 0);
  let awayScore = Number(md.away?.score ?? 0);
  if (Number.isNaN(homeScore)) homeScore = 0;
  if (Number.isNaN(awayScore)) awayScore = 0;

  const refs = getMatchTeamRefs(md);
  const streamTeamIdMap = buildStreamTeamIdToCardTeamIdMap(
    streamEvents,
    refs.homeTeamId,
    refs.awayTeamId,
  );

  let changed = false;

  streamEvents.forEach((event) => {
    const payload = event?.payload;
    if (!payload || typeof payload !== 'object') return;

    const actionId =
      payload.actionId != null ? String(payload.actionId).trim() : '';
    if (actionId && skipGoalActionIds.has(actionId)) return;

    const rawType =
      payload.type != null ? String(payload.type).trim().toUpperCase() : '';

    if (payload.homeScore != null && payload.awayScore != null) {
      const h = Number(payload.homeScore);
      const a = Number(payload.awayScore);
      if (!Number.isNaN(h) && !Number.isNaN(a)) {
        homeScore = h;
        awayScore = a;
        changed = true;
      }
      return;
    }

    if (!GOAL_ACTION_TYPES.has(rawType)) return;

    const side = resolveActionTeamSide(payload, md, streamTeamIdMap);
    const creditHome =
      side === 'home' ? rawType !== 'OWN_GOAL' : side === 'away' && rawType === 'OWN_GOAL';
    const creditAway =
      side === 'away' ? rawType !== 'OWN_GOAL' : side === 'home' && rawType === 'OWN_GOAL';

    if (creditHome) {
      homeScore += 1;
      changed = true;
    } else if (creditAway) {
      awayScore += 1;
      changed = true;
    }
  });

  if (!changed) return;

  writeMatchCardScores(patch, card, { home: homeScore, away: awayScore });

  reconcileMatchCardScoreWithStoredGoals(patch, patch.matchCard ?? currentMatchCard, {
    lineups: options.lineups,
  });
}
